/**
 * Voluum-style Tracking header: title, domain field, GTM toggle.
 * For Media Devoted | Created By Ciro Autiero
 */

import { ChevronsUpDown, HelpCircle } from 'lucide-react';

import { Input } from '@/components/ui/form/input/Input';
import { Switch } from '@/components/ui/form/controls/Switch';
import { Tooltip } from '@/components/ui/Tooltip';

import type { FC } from 'react';
import type { VoluumLanderFormValues } from '../map-voluum-lander-form';

const labelCls = 'text-xs font-semibold text-muted-foreground';

const DOMAIN_HINT =
  'Host used for Voluum click URLs and tracking scripts (preferred tracking domain).';

const GTM_HINT =
  'When enabled, manage Voluum tracking via Google Tag Manager instead of pasting scripts on the lander.';

export interface LanderTrackingTopSectionProps {
  values: VoluumLanderFormValues;
  patch: (p: Partial<VoluumLanderFormValues>) => void;
  disabled?: boolean;
}

const LanderTrackingTopSection: FC<LanderTrackingTopSectionProps> = ({
  values,
  patch,
  disabled,
}) => (
  <div className="space-y-4">
    <h2 className="text-base font-bold text-foreground">Tracking</h2>
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label
          htmlFor="lander-tracking-domain-input"
          className={labelCls}
        >
          Tracking domain
        </label>
        <Tooltip content={DOMAIN_HINT}>
          <span className="inline-flex cursor-help text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" aria-hidden />
          </span>
        </Tooltip>
      </div>
      <div className="relative">
        <Input
          id="lander-tracking-domain-input"
          value={values.preferredTrackingDomain}
          disabled={disabled}
          onChange={(e) =>
            patch({ preferredTrackingDomain: e.target.value })
          }
          placeholder="e.g. incessivethelds.com"
          className="h-9 pr-9 font-mono text-sm"
          autoComplete="off"
        />
        <ChevronsUpDown
          className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-primary"
          aria-hidden
        />
      </div>
    </div>
    <div className="flex items-center gap-2.5">
      <Switch
        id="lander-gtm"
        checked={values.useGtmForVoluumScripts}
        disabled={disabled}
        onCheckedChange={(v) => patch({ useGtmForVoluumScripts: v })}
        aria-label="Use GTM to manage Voluum tracking scripts"
      />
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <label
          htmlFor="lander-gtm"
          className="cursor-pointer text-sm leading-snug text-foreground"
        >
          Use GTM to manage Voluum tracking scripts
        </label>
        <Tooltip content={GTM_HINT}>
          <span className="inline-flex shrink-0 cursor-help text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" aria-hidden />
          </span>
        </Tooltip>
      </div>
    </div>
  </div>
);

export default LanderTrackingTopSection;
